import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Badge, Button, Card, cn, Field, SelectField } from '@pixlide/ui';
import {
  useAddTemplateField,
  useArchiveTemplate,
  usePublishTemplate,
  useSetTemplateFieldEnabled,
  useSetTemplatePremium,
  useUpdateTemplate,
  type TemplateAdminListItem,
  type TemplateAnimationInspection,
} from '@/api/queries/templates';
import type { components } from '@/api/generated/schema';
import { InspectionSummary } from '@/features/templates/InspectionSummary';
import { TemplateAuditPanel } from '@/features/templates/TemplateAuditPanel';
import { TemplateFilesPanel } from '@/features/templates/TemplateFilesPanel';
import { TemplatePreviewPanel } from '@/features/templates/TemplatePreviewPanel';
import { MAX_DURATION_SECONDS, MIN_DURATION_SECONDS } from '@/features/templates/template-files';
import {
  errorMessage,
  fieldKindLabels,
  formatAspectClasses,
  formatLabels,
  statusLabels,
  statusTones,
} from '@/features/templates/template-ui';

/**
 * Mêmes bornes que `Template` côté domaine, qui refuserait au-delà : la validation est ici
 * pour donner un retour immédiat, jamais pour tenir lieu de règle métier.
 */
const updateTemplateSchema = z.object({
  name: z.string().min(1, 'Le nom est obligatoire.').max(150, 'Le nom est limité à 150 caractères.'),
  description: z.string().max(2000, 'La description est limitée à 2 000 caractères.').optional(),
  category: z.string().min(1, 'La catégorie est obligatoire.').max(100, 'La catégorie est limitée à 100 caractères.'),
  durationSeconds: z.coerce.number().int().min(MIN_DURATION_SECONDS).max(MAX_DURATION_SECONDS),
});

const addFieldSchema = z.object({
  key: z
    .string()
    .min(1, 'La clé est obligatoire.')
    .regex(/^[a-z0-9_]+$/, 'Lettres minuscules, chiffres et underscores uniquement.'),
  label: z.string().min(1, 'Le libellé est obligatoire.'),
  kind: z.coerce.number().int().min(0).max(5),
  isRequired: z.boolean().optional(),
  maxLength: z.coerce.number().int().positive().optional(),
  defaultValue: z.string().optional(),
});

type Panel = 'edit' | 'audit' | 'preview' | 'field' | null;

export function TemplateCard({ template }: { template: TemplateAdminListItem }) {
  // Un seul panneau ouvert à la fois : la carte restait sinon une pile de formulaires
  // empilés, et deux formulaires ouverts mélangeaient visuellement leurs erreurs.
  const [panel, setPanel] = useState<Panel>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [inspection, setInspection] = useState<TemplateAnimationInspection | null>(null);

  const addField = useAddTemplateField();
  const publishTemplate = usePublishTemplate();
  const archiveTemplate = useArchiveTemplate();
  const setPremium = useSetTemplatePremium();
  const setFieldEnabled = useSetTemplateFieldEnabled();
  const updateTemplate = useUpdateTemplate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(addFieldSchema),
    defaultValues: { kind: 0, isRequired: false },
  });

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors, isSubmitting: isEditSubmitting },
  } = useForm({
    resolver: zodResolver(updateTemplateSchema),
    defaultValues: {
      name: template.name,
      description: template.description ?? '',
      category: template.category,
      durationSeconds: template.durationSeconds,
    },
  });

  const toggle = (next: Exclude<Panel, null>) => {
    // Le formulaire est repeuplé À CHAQUE ouverture depuis le template courant : rouvrir
    // après une modification faite ailleurs ne doit pas réafficher d'anciennes valeurs.
    if (next === 'edit' && panel !== 'edit') {
      resetEdit({
        name: template.name,
        description: template.description ?? '',
        category: template.category,
        durationSeconds: template.durationSeconds,
      });
    }

    setPanel((current) => (current === next ? null : next));
  };

  const onAddField = handleSubmit(async (values) => {
    await addField
      .mutateAsync({
        templateId: template.id,
        key: values.key,
        label: values.label,
        kind: values.kind as components['schemas']['PixlideDomainContentEnumsTemplateFieldKind'],
        isRequired: values.isRequired ?? false,
        maxLength: values.maxLength ?? null,
        defaultValue: values.defaultValue || null,
      })
      .then(() => reset())
      .catch(() => undefined); // Message rendu depuis `addField.isError`.
  });

  const onUpdate = handleEditSubmit(async (values) => {
    await updateTemplate
      .mutateAsync({
        templateId: template.id,
        name: values.name,
        description: values.description?.trim() ? values.description : null,
        category: values.category,
        durationSeconds: values.durationSeconds,
      })
      .then(() => setPanel(null))
      .catch(() => undefined); // Message rendu depuis `updateTemplate.isError`.
  });

  const isDraft = template.status === 0;
  const enabledFields = template.fields.filter((field) => field.isEnabled).length;

  // Raisons pour lesquelles la publication est impossible, mêmes règles que `Template.Publish` :
  // un bouton grisé sans explication obligeait l'admin à deviner ce qui manquait.
  const publishBlockers = [
    !template.isAnimationSourceUploaded && 'animation confirmée',
    !template.isThumbnailUploaded && 'vignette confirmée',
    enabledFields === 0 && 'au moins un champ modifiable',
  ].filter((blocker): blocker is string => typeof blocker === 'string');

  return (
    <Card className="flex flex-col gap-3 overflow-hidden p-0">
      {template.thumbnailUrl ? (
        <div className={cn('max-h-56 w-full overflow-hidden bg-ink-50', formatAspectClasses[template.format])}>
          <img src={template.thumbnailUrl} alt="" loading="lazy" className="size-full object-cover" />
        </div>
      ) : null}

      <div className="flex flex-col gap-3 p-6 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold">{template.name}</h3>
            <p className="text-sm text-ink-500">{template.category}</p>
          </div>
          <Badge tone={statusTones[template.status]}>{statusLabels[template.status]}</Badge>
        </div>

        <p className="text-sm text-ink-500">
          {formatLabels[template.format]} · {template.durationSeconds} s · {template.fieldCount} champ
          {template.fieldCount > 1 ? 's' : ''} modifiable{template.fieldCount > 1 ? 's' : ''}
          {template.fields.length > template.fieldCount ? ` sur ${template.fields.length}` : ''}
        </p>

        {template.description ? <p className="text-sm text-ink-700">{template.description}</p> : null}

        {!template.isAnimationSourceUploaded ? (
          <p className="text-xs text-warning-500">Animation non confirmée</p>
        ) : null}
        {!template.isThumbnailUploaded ? <p className="text-xs text-warning-500">Vignette non confirmée</p> : null}

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="size-4 rounded"
            checked={template.isPremium}
            disabled={setPremium.isPending}
            onChange={(event) => setPremium.mutate({ templateId: template.id, isPremium: event.target.checked })}
          />
          Réservé aux formules payantes
        </label>
        {setPremium.isError ? (
          <p role="alert" className="text-xs font-medium text-danger-500">
            {errorMessage(setPremium.error, "Impossible de modifier l'accès.")}
          </p>
        ) : null}

        {template.fields.length > 0 ? (
          <fieldset className="flex flex-col gap-1.5 rounded-lg bg-ink-50 p-3">
            <legend className="px-1 text-xs font-medium text-ink-500">Champs proposés au client</legend>

            {/* Décocher fige le champ : le calque garde la valeur d'origine de l'animation et
                disparaît du formulaire client. Réversible, y compris sur un template publié. */}
            {template.fields.map((field) => (
              <label key={field.key} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="size-4 rounded"
                  checked={field.isEnabled}
                  disabled={setFieldEnabled.isPending}
                  onChange={(event) =>
                    setFieldEnabled.mutate({
                      templateId: template.id,
                      key: field.key,
                      isEnabled: event.target.checked,
                    })
                  }
                />
                <span className={field.isEnabled ? undefined : 'text-ink-300 line-through'}>{field.label}</span>
                <span className="text-xs text-ink-300">{fieldKindLabels[field.kind]}</span>
                <code className="text-xs text-ink-300">{field.key}</code>
                {field.isRequired ? <span className="text-xs text-ink-500">obligatoire</span> : null}
              </label>
            ))}

            {/* Le domaine refuse de figer le dernier champ modifiable : sans ce message, la
                case reviendrait seule à son état initial sans explication. */}
            {setFieldEnabled.isError ? (
              <p role="alert" className="text-xs font-medium text-danger-500">
                {errorMessage(setFieldEnabled.error, 'Impossible de modifier ce champ.')}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {/* Modifiable même publié : corriger un nom affiché aux clients ne doit pas exiger
              d'archiver le template et de tout recréer. */}
          <Button variant="ghost" size="sm" aria-expanded={panel === 'edit'} onClick={() => toggle('edit')}>
            {panel === 'edit' ? 'Annuler' : 'Modifier'}
          </Button>

          {template.isAnimationSourceUploaded ? (
            <Button variant="ghost" size="sm" aria-expanded={panel === 'preview'} onClick={() => toggle('preview')}>
              {panel === 'preview' ? 'Masquer l’aperçu' : 'Aperçu'}
            </Button>
          ) : null}

          <Button variant="ghost" size="sm" aria-expanded={panel === 'audit'} onClick={() => toggle('audit')}>
            {panel === 'audit' ? 'Masquer l’historique' : 'Historique'}
          </Button>

          {isDraft ? (
            <Button variant="ghost" size="sm" aria-expanded={panel === 'field'} onClick={() => toggle('field')}>
              {panel === 'field' ? 'Fermer' : 'Ajouter un champ'}
            </Button>
          ) : null}
        </div>

        {panel === 'edit' ? (
          <form onSubmit={onUpdate} noValidate className="grid gap-3 rounded-lg bg-ink-50 p-3">
            <Field label="Nom" error={editErrors.name?.message} {...registerEdit('name')} />
            <Field
              label="Description (optionnel)"
              error={editErrors.description?.message}
              {...registerEdit('description')}
            />
            <Field label="Catégorie" error={editErrors.category?.message} {...registerEdit('category')} />
            <Field
              label="Durée (secondes)"
              type="number"
              min={MIN_DURATION_SECONDS}
              max={MAX_DURATION_SECONDS}
              error={editErrors.durationSeconds?.message}
              {...registerEdit('durationSeconds')}
            />

            {/* Ni le format ni le schéma de champs : ils forment le contrat des projets clients
                déjà créés à partir de ce template. */}
            <p className="text-xs text-ink-500">
              Le format et les champs ne sont pas modifiables : les vidéos déjà créées avec ce
              template s’appuient dessus.
            </p>

            {updateTemplate.isError ? (
              <p role="alert" className="text-xs font-medium text-danger-500">
                {errorMessage(updateTemplate.error, "Impossible d'enregistrer les modifications.")}
              </p>
            ) : null}

            <Button type="submit" size="sm" isLoading={isEditSubmitting || updateTemplate.isPending}>
              Enregistrer
            </Button>
          </form>
        ) : null}

        {panel === 'preview' ? <TemplatePreviewPanel templateId={template.id} format={template.format} /> : null}

        {panel === 'audit' ? <TemplateAuditPanel templateId={template.id} /> : null}

        {panel === 'field' ? (
          <form onSubmit={onAddField} noValidate className="grid gap-3 rounded-lg bg-ink-50 p-3">
            <Field
              label="Clé du slot"
              error={errors.key?.message}
              hint="Identifiant du slot : slot-text-prix → prix, slot-color-fond → fond_color."
              {...register('key')}
            />
            <Field label="Libellé" error={errors.label?.message} {...register('label')} />

            <SelectField label="Type" {...register('kind')}>
              {Object.entries(fieldKindLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" className="size-4 rounded" {...register('isRequired')} />
              Obligatoire
            </label>

            <Field
              label="Longueur maximale (optionnel)"
              type="number"
              min={1}
              error={errors.maxLength?.message}
              {...register('maxLength')}
            />
            <Field label="Valeur par défaut (optionnel)" {...register('defaultValue')} />

            {addField.isError ? (
              <p role="alert" className="text-xs font-medium text-danger-500">
                {errorMessage(addField.error, "Impossible d'ajouter le champ.")}
              </p>
            ) : null}

            <Button type="submit" size="sm" isLoading={isSubmitting || addField.isPending}>
              Ajouter le champ
            </Button>
          </form>
        ) : null}

        {isDraft ? (
          <TemplateFilesPanel
            templateId={template.id}
            isAnimationConfirmed={template.isAnimationSourceUploaded}
            isThumbnailConfirmed={template.isThumbnailUploaded}
            onInspected={setInspection}
          />
        ) : null}

        {inspection ? <InspectionSummary inspection={inspection} onClose={() => setInspection(null)} /> : null}

        {isDraft ? (
          <div className="mt-auto flex flex-col gap-1.5">
            {publishBlockers.length > 0 ? (
              <p className="text-xs text-ink-500">Pour publier, il manque : {publishBlockers.join(', ')}.</p>
            ) : null}

            <Button
              size="sm"
              disabled={publishBlockers.length > 0}
              isLoading={publishTemplate.isPending}
              onClick={() => publishTemplate.mutate(template.id)}
            >
              Publier
            </Button>

            {publishTemplate.isError ? (
              <p role="alert" className="text-xs font-medium text-danger-500">
                {errorMessage(publishTemplate.error, 'Impossible de publier ce template.')}
              </p>
            ) : null}
          </div>
        ) : null}

        {template.status !== 2 ? (
          confirmArchive ? (
            // Confirmation en deux temps : l'archivage retire le template de la bibliothèque
            // des clients et n'a pas de bouton « désarchiver ».
            <div className="flex flex-col gap-2 rounded-lg bg-ink-50 p-3">
              <p className="text-xs text-ink-700">
                Archiver retire ce template de la bibliothèque des clients. Les vidéos déjà créées
                restent intactes.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={archiveTemplate.isPending}
                  onClick={() => archiveTemplate.mutate(template.id, { onSuccess: () => setConfirmArchive(false) })}
                >
                  Confirmer l’archivage
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setConfirmArchive(true)}>
              Archiver
            </Button>
          )
        ) : null}

        {archiveTemplate.isError ? (
          <p role="alert" className="text-xs font-medium text-danger-500">
            {errorMessage(archiveTemplate.error, "Impossible d'archiver ce template.")}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
